// 亡魂效果處理器
// Soul Effect Handlers - 亡魂點數、還魂（骷髏族核心機制）

import type { GameState, BattlefieldSlot, Card, CardDefinition } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('SoulHandlers');

/**
 * 獲取玩家 Key
 */
function getPlayerKey(state: GameState, playerId?: string): 'player1' | 'player2' {
  if (playerId) {
    return playerId === state.player1.id ? 'player1' : 'player2';
  }
  return state.activePlayerId === state.player1.id ? 'player1' : 'player2';
}

/**
 * 計算卡牌的亡魂點數
 * 預設規則：攻擊力 = 亡魂點數（可被 effectText 覆蓋）
 */
function getSoulValue(card: Card, state: GameState): number {
  const cardDefinitions = state.cardDefinitions;

  // 優先從 CardDefinition 獲取
  if (cardDefinitions) {
    const def = cardDefinitions.get(card.definitionId);
    if (def) {
      // 如果有明確定義的亡魂點數
      const extendedDef = def as CardDefinition & { soulValue?: number };
      if (typeof extendedDef.soulValue === 'number') {
        return extendedDef.soulValue;
      }
      // 否則使用攻擊力作為亡魂點數
      if (typeof def.atk === 'number') {
        return def.atk;
      }
    }
  }

  // 如果沒有定義，嘗試從 activeEffects 獲取
  const soulEffect = card.activeEffects?.find(e => e.startsWith('soul:'));
  if (soulEffect) {
    const value = parseInt(soulEffect.split(':')[1], 10);
    if (!isNaN(value)) {
      return value;
    }
  }

  // 預設返回 0
  return 0;
}

/**
 * 亡魂標記處理器
 * 處理「此卡具有X亡魂」效果
 */
export class SoulHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('亡魂') && !text.includes('還魂');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「此卡具有X亡魂」
    const soulMatch = effectText.match(/(?:此卡)?(?:具有|擁有)?(\d+)(?:點)?亡魂/);
    if (soulMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'markSoul',
        value: parseInt(soulMatch[1], 10),
      });
    }

    // 解析「墓地中每有1張亡靈單位，+X」之類的計數效果
    const countMatch = effectText.match(/墓地中每有(\d+)(?:張|個)?(.+?)(?:，|,)?(.+)/);
    if (countMatch && effectText.includes('亡魂')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'countSouls',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'markSoul') {
      const sourceId = action.sourceCardId;
      const soulValue = typeof action.value === 'number' ? action.value : 0;

      if (!sourceId) {
        log.debug('缺少來源卡牌 ID');
        return state;
      }

      // 標記卡牌的亡魂點數
      // 這通常在卡牌進入墓地時處理
      log.debug(`${sourceId} 標記亡魂點數: ${String(soulValue)}`);
      return state;
    }

    if (action.customHandler === 'countSouls') {
      const playerKey = getPlayerKey(state);
      const graveyard = state[playerKey].graveyard;

      // 計算墓地中的亡魂總數
      let totalSouls = 0;
      for (const card of graveyard) {
        totalSouls += getSoulValue(card, state);
      }

      log.debug(`墓地亡魂總數: ${String(totalSouls)}`);
      return state;
    }

    return state;
  }
}

/**
 * 還魂處理器
 * 處理「將墓地中總和達特定數值的亡魂卡回手牌」
 */
export class ReviveHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('還魂') ||
           (text.includes('亡魂') && text.includes('回手牌'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「還魂X：將墓地中亡魂總和為X的卡牌回手牌」
    const reviveMatch = effectText.match(/還魂(\d+)/);
    if (reviveMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'revive',
        value: parseInt(reviveMatch[1], 10),
        requiresChoice: true,
      });
    }

    // 解析「將墓地中亡魂總和為X的單位回手牌」
    const returnMatch = effectText.match(/墓地中亡魂總和(?:為|達到?)(\d+)(?:的單位)?回手牌/);
    if (returnMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'revive',
        value: parseInt(returnMatch[1], 10),
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'revive') {
      return state;
    }

    const targetValue = typeof action.value === 'number' ? action.value : 0;
    const selectedCardIds = action.targetCardIds || [];

    if (selectedCardIds.length === 0) {
      log.debug('未選擇任何卡牌');
      return state;
    }

    const playerKey = getPlayerKey(state);
    const player = state[playerKey];
    const graveyard = [...player.graveyard];

    // 找到選擇的卡牌
    const selectedCards = graveyard.filter(c => selectedCardIds.includes(c.id));

    // 計算選擇卡牌的亡魂總和
    let totalSouls = 0;
    for (const card of selectedCards) {
      totalSouls += getSoulValue(card, state);
    }

    // 驗證亡魂總和是否符合要求
    if (totalSouls !== targetValue) {
      log.debug(`亡魂總和 ${String(totalSouls)} 不等於目標值 ${String(targetValue)}`);
      return state;
    }

    // 從墓地移除選擇的卡牌
    const newGraveyard = graveyard.filter(c => !selectedCardIds.includes(c.id));

    // 將卡牌加入手牌
    const cardsToHand = selectedCards.map(c => ({
      ...c,
      location: 'hand' as const,
    }));

    const newState: GameState = {
      ...state,
      [playerKey]: {
        ...player,
        graveyard: newGraveyard,
        hand: [...player.hand, ...cardsToHand],
      },
    };

    log.debug(`還魂成功！${String(selectedCards.length)} 張卡牌回到手牌`);

    return newState;
  }
}

/**
 * 亡魂犧牲處理器
 * 處理「犧牲墓地中X亡魂」效果
 */
export class SoulSacrificeHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('犧牲') && text.includes('亡魂')) ||
           (text.includes('除外') && text.includes('亡魂'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「犧牲X亡魂」
    const sacrificeMatch = effectText.match(/(?:犧牲|除外)(\d+)(?:點)?亡魂/);
    if (sacrificeMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'soulSacrifice',
        value: parseInt(sacrificeMatch[1], 10),
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'soulSacrifice') {
      return state;
    }

    const targetValue = typeof action.value === 'number' ? action.value : 0;
    const selectedCardIds = action.targetCardIds || [];

    if (selectedCardIds.length === 0) {
      log.debug('未選擇任何卡牌');
      return state;
    }

    const playerKey = getPlayerKey(state);
    const player = state[playerKey];
    const graveyard = [...player.graveyard];

    // 找到選擇的卡牌
    const selectedCards = graveyard.filter(c => selectedCardIds.includes(c.id));

    // 計算選擇卡牌的亡魂總和
    let totalSouls = 0;
    for (const card of selectedCards) {
      totalSouls += getSoulValue(card, state);
    }

    // 驗證亡魂總和是否足夠
    if (totalSouls < targetValue) {
      log.debug(`亡魂總和 ${String(totalSouls)} 不足目標值 ${String(targetValue)}`);
      return state;
    }

    // 從墓地移除選擇的卡牌（被除外）
    const newGraveyard = graveyard.filter(c => !selectedCardIds.includes(c.id));

    // 注意：被犧牲的亡魂卡被「除外」，不是回到牌庫或手牌
    // 這裡簡化處理，直接移除

    const newState: GameState = {
      ...state,
      [playerKey]: {
        ...player,
        graveyard: newGraveyard,
      },
    };

    log.debug(`犧牲了 ${String(totalSouls)} 點亡魂（${String(selectedCards.length)} 張卡牌）`);

    return newState;
  }
}

/**
 * 亡魂強化處理器
 * 處理「依據墓地亡魂數量強化」效果
 */
export class SoulBuffHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('亡魂') && (text.includes('+') || text.includes('攻擊力'))) ||
           text.includes('每1點亡魂');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「墓地中每1點亡魂，此卡+1攻擊力」
    const buffMatch = effectText.match(/(?:墓地中)?每(\d+)(?:點)?亡魂[，,]?(?:此卡)?[+＋](\d+)攻擊力/);
    if (buffMatch) {
      // 編碼為 "perSoul:buffAmount" 格式
      const perSoul = parseInt(buffMatch[1], 10);
      const buffAmount = parseInt(buffMatch[2], 10);
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'soulBuff',
        value: `${String(perSoul)}:${String(buffAmount)}`,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'soulBuff') {
      return state;
    }

    const sourceId = action.sourceCardId;
    if (!sourceId) {
      log.debug('缺少來源卡牌 ID');
      return state;
    }

    // 解析 "perSoul:buffAmount" 格式
    const valueStr = action.value as string | undefined;
    if (!valueStr || typeof valueStr !== 'string') {
      return state;
    }

    const [perSoulStr, buffAmountStr] = valueStr.split(':');
    const perSoul = parseInt(perSoulStr, 10);
    const buffAmount = parseInt(buffAmountStr, 10);

    if (isNaN(perSoul) || isNaN(buffAmount)) {
      return state;
    }

    const playerKey = getPlayerKey(state);
    const graveyard = state[playerKey].graveyard;

    // 計算墓地中的亡魂總數
    let totalSouls = 0;
    for (const card of graveyard) {
      totalSouls += getSoulValue(card, state);
    }

    // 計算增益
    const buffMultiplier = Math.floor(totalSouls / perSoul);
    const totalBuff = buffMultiplier * buffAmount;

    if (totalBuff === 0) {
      log.debug('無增益效果');
      return state;
    }

    // 更新卡牌的攻擊力
    const mapSlot = (slot: BattlefieldSlot) =>
      slot.unit?.id === sourceId
        ? {
            ...slot,
            unit: {
              ...slot.unit,
              atkModifier: (slot.unit.atkModifier || 0) + totalBuff,
            },
          }
        : slot;

    const newState: GameState = {
      ...state,
      battlefield: {
        player1Side: {
          frontLine: state.battlefield.player1Side.frontLine.map(mapSlot),
          backLine: state.battlefield.player1Side.backLine.map(mapSlot),
        },
        player2Side: {
          frontLine: state.battlefield.player2Side.frontLine.map(mapSlot),
          backLine: state.battlefield.player2Side.backLine.map(mapSlot),
        },
      },
    };

    log.debug(`${sourceId} 獲得 +${String(totalBuff)} 攻擊力（墓地亡魂: ${String(totalSouls)}）`);

    return newState;
  }
}
